import { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw, Loader2, UserPlus, Check, X as XIcon, MailQuestion, AlertCircle, ChevronDown,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SolicitudesTabs } from '@/components/SolicitudesTabs';

interface JoinRequestRow {
  id: string;
  account_id: string;
  profile_id: string;
  requested_role: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  message: string | null;
  created_at: string;
  /** Joined view columns: */
  requester_full_name?: string | null;
  requester_email?: string | null;
  requester_phone?: string | null;
  account_name?: string | null;
  account_number?: string | null;
}

const STATUS_STYLES: Record<JoinRequestRow['status'], string> = {
  pending:   'bg-amber-500/10 text-amber-600',
  approved:  'bg-green-500/10 text-green-600',
  rejected:  'bg-red-500/10 text-red-600',
  cancelled: 'bg-gray-500/10 text-gray-500',
};

const STATUS_LABELS: Record<JoinRequestRow['status'], string> = {
  pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada', cancelled: 'Cancelada',
};

export function JoinRequestsPage() {
  const [requests, setRequests] = useState<JoinRequestRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [busyId, setBusyId]     = useState<string | null>(null);
  const [filter, setFilter]     = useState<'pending' | 'all'>('pending');
  // Tracks which request is in "choose role before approving" state
  const [approvalState, setApprovalState] = useState<{ id: string; role: string } | null>(null);

  const ROLE_OPTIONS = [
    { value: 'worker',     label: 'Empleado' },
    { value: 'manager',    label: 'Encargado' },
    { value: 'technician', label: 'Técnico' },
    { value: 'familiar',   label: 'Familiar' },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The view is RLS-protected: returns rows where caller is owner/manager
      // of the target account OR the requester themselves.
      let q = supabase.from('v_my_account_join_requests').select('*').order('created_at', { ascending: false });
      if (filter === 'pending') q = q.eq('status', 'pending');
      const { data, error: selErr } = await q;
      if (selErr) throw selErr;
      setRequests((data ?? []) as JoinRequestRow[]);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? 'Error cargando solicitudes');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  const respond = async (id: string, action: 'approve' | 'reject', role?: string) => {
    setBusyId(id);
    setApprovalState(null);
    try {
      const body: Record<string, string> = { request_id: id, action };
      if (action === 'approve' && role) body.role = role;
      const { data, error: fnErr } = await supabase.functions.invoke('respond-join-request', { body });
      if (fnErr) throw fnErr;
      if (!data?.ok) throw new Error(data?.error ?? 'Error respondiendo solicitud');
      await load();
    } catch (e: unknown) {
      alert((e as { message?: string })?.message ?? 'Error respondiendo solicitud');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <SolicitudesTabs />
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MailQuestion className="w-6 h-6" /> Solicitudes de unión
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Personas que pidieron acceso a tus cuentas usando tu correo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-9 rounded-md border bg-background px-3 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'pending' | 'all')}
          >
            <option value="pending">Solo pendientes</option>
            <option value="all">Todas</option>
          </select>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {filter === 'pending' ? 'Pendientes' : 'Todas'} ({requests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <p className="text-sm text-destructive flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {error}</p>
          ) : requests.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No hay solicitudes.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div
                  key={r.id}
                  className="border rounded-lg p-4 flex items-start gap-4 hover:bg-muted/30 transition"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{r.requester_full_name ?? r.requester_email ?? r.profile_id}</span>
                      <span className="text-xs text-muted-foreground">{r.requester_email}</span>
                      <Badge className={STATUS_STYLES[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Quiere unirse a{' '}
                      <strong>{r.account_number ?? '—'} · {r.account_name ?? r.account_id}</strong>
                    </p>
                    {r.message && (
                      <p className="text-sm mt-2 italic border-l-2 border-muted-foreground/30 pl-3">
                        "{r.message}"
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {approvalState?.id === r.id ? (
                        // Role selector expanded
                        <div className="flex flex-col gap-2">
                          <select
                            className="h-8 rounded-md border bg-background px-2 text-sm"
                            value={approvalState.role}
                            onChange={(e) => setApprovalState({ id: r.id, role: e.target.value })}
                          >
                            {ROLE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            onClick={() => void respond(r.id, 'approve', approvalState.role)}
                            disabled={busyId === r.id}
                          >
                            {busyId === r.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                            Confirmar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setApprovalState(null)}
                            disabled={busyId === r.id}
                          >
                            <XIcon className="w-3 h-3 mr-1" /> Cancelar
                          </Button>
                        </div>
                      ) : (
                        // Default buttons
                        <>
                          <Button
                            size="sm"
                            onClick={() => setApprovalState({ id: r.id, role: r.requested_role })}
                            disabled={busyId === r.id}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Aprobar <ChevronDown className="w-3 h-3 ml-1" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void respond(r.id, 'reject')}
                            disabled={busyId === r.id}
                          >
                            {busyId === r.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XIcon className="w-3 h-3 mr-1" />}
                            Rechazar
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
